import React, { useState, useEffect } from 'react'
import {
  Container,
  Header,
  SpaceBetween,
  FormField,
  Multiselect,
  Toggle,
  Button,
  Alert,
  Box,
  ExpandableSection,
  Grid,
  ColumnLayout
} from '@cloudscape-design/components'
import { useMutation } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useAmplifyAuth } from '../../../providers/AmplifyAuthProvider'
import { Id } from '../../../../convex/_generated/dataModel'

interface DashboardPermissionsProps {
  dashboardId: Id<"dashboards">
  currentPermissions?: {
    roles?: {
      view?: string[]
      edit?: string[]
      admin?: string[]
    }
    departments?: {
      view?: string[]
      edit?: string[]
    }
    users?: {
      view?: Id<"users">[]
      edit?: Id<"users">[]
    }
  }
  isPublic?: boolean
  onPermissionsUpdated?: () => void
}

// Available roles and departments
const AVAILABLE_ROLES = [
  { label: 'Operator', value: 'operator' },
  { label: 'Supervisor', value: 'supervisor' },
  { label: 'Engineer', value: 'engineer' },
  { label: 'Manager', value: 'manager' },
  { label: 'Admin', value: 'admin' }
]

const AVAILABLE_DEPARTMENTS = [
  { label: 'Production', value: 'production' },
  { label: 'Maintenance', value: 'maintenance' },
  { label: 'Quality Control', value: 'quality' },
  { label: 'Engineering', value: 'engineering' },
  { label: 'Operations', value: 'operations' }
]

export const DashboardPermissions: React.FC<DashboardPermissionsProps> = ({
  dashboardId,
  currentPermissions,
  isPublic = false,
  onPermissionsUpdated
}) => {
  const { sessionId } = useAmplifyAuth()
  const updatePermissions = useMutation(api.components.dashboards.index.updateDashboardPermissions)
  
  // State for permissions
  const [roleViewPermissions, setRoleViewPermissions] = useState<string[]>([])
  const [roleEditPermissions, setRoleEditPermissions] = useState<string[]>([])
  const [deptViewPermissions, setDeptViewPermissions] = useState<string[]>([])
  const [deptEditPermissions, setDeptEditPermissions] = useState<string[]>([])
  const [publicAccess, setPublicAccess] = useState(isPublic)
  
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Initialize state from current permissions
  useEffect(() => {
    if (currentPermissions) {
      setRoleViewPermissions(currentPermissions.roles?.view || [])
      setRoleEditPermissions(currentPermissions.roles?.edit || [])
      setDeptViewPermissions(currentPermissions.departments?.view || [])
      setDeptEditPermissions(currentPermissions.departments?.edit || [])
    }
    setPublicAccess(isPublic)
  }, [currentPermissions, isPublic])

  const handleSavePermissions = async () => {
    if (!sessionId) return
    
    setIsLoading(true)
    setError(null)
    setSuccess(null)
    
    try {
      const permissions = {
        roles: {
          view: roleViewPermissions.length > 0 ? roleViewPermissions : undefined,
          edit: roleEditPermissions.length > 0 ? roleEditPermissions : undefined
        },
        departments: {
          view: deptViewPermissions.length > 0 ? deptViewPermissions : undefined,
          edit: deptEditPermissions.length > 0 ? deptEditPermissions : undefined
        }
      }

      await updatePermissions({
        sessionId,
        dashboardId,
        permissions
      })

      setSuccess('Permissions updated successfully!')
      if (onPermissionsUpdated) {
        onPermissionsUpdated()
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update permissions')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClearPermissions = () => {
    setRoleViewPermissions([])
    setRoleEditPermissions([])
    setDeptViewPermissions([])
    setDeptEditPermissions([])
  }

  return (
    <Container>
      <SpaceBetween size="l">
        <Header
          variant="h2"
          description="Control who can access and modify this dashboard"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button 
                variant="link" 
                onClick={handleClearPermissions}
                disabled={isLoading}
              >
                Clear All
              </Button>
              <Button 
                variant="primary" 
                onClick={handleSavePermissions}
                loading={isLoading}
                disabled={isLoading}
              >
                Save Permissions
              </Button>
            </SpaceBetween>
          }
        >
          Dashboard Access Control
        </Header>

        {error && (
          <Alert statusIconAriaLabel="Error" type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert statusIconAriaLabel="Success" type="success" dismissible onDismiss={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Public Access Toggle */}
        <FormField 
          label="Public Access"
          description="When enabled, all users can view this dashboard"
        >
          <Toggle
            checked={publicAccess}
            onChange={(e) => setPublicAccess(e.detail.checked)}
            disabled={isLoading}
          >
            Make dashboard public
          </Toggle>
        </FormField>

        {/* Role-Based Permissions */}
        <ExpandableSection headerText="Role-Based Permissions" defaultExpanded>
          <SpaceBetween size="m">
            <ColumnLayout columns={2}>
              <FormField 
                label="View Access"
                description="Users with these roles can view the dashboard"
              >
                <Multiselect
                  selectedOptions={roleViewPermissions.map(role => ({ label: AVAILABLE_ROLES.find(r => r.value === role)?.label || role, value: role }))}
                  onChange={(e) => setRoleViewPermissions(e.detail.selectedOptions.map(option => option.value!))}
                  options={AVAILABLE_ROLES}
                  placeholder="Select roles for view access"
                  disabled={isLoading}
                />
              </FormField>

              <FormField 
                label="Edit Access"
                description="Users with these roles can edit the dashboard"
              >
                <Multiselect
                  selectedOptions={roleEditPermissions.map(role => ({ label: AVAILABLE_ROLES.find(r => r.value === role)?.label || role, value: role }))}
                  onChange={(e) => setRoleEditPermissions(e.detail.selectedOptions.map(option => option.value!))}
                  options={AVAILABLE_ROLES}
                  placeholder="Select roles for edit access"
                  disabled={isLoading}
                />
              </FormField>
            </ColumnLayout>
          </SpaceBetween>
        </ExpandableSection>

        {/* Department-Based Permissions */}
        <ExpandableSection headerText="Department-Based Permissions">
          <SpaceBetween size="m">
            <ColumnLayout columns={2}>
              <FormField 
                label="View Access"
                description="Users in these departments can view the dashboard"
              >
                <Multiselect
                  selectedOptions={deptViewPermissions.map(dept => ({ label: AVAILABLE_DEPARTMENTS.find(d => d.value === dept)?.label || dept, value: dept }))}
                  onChange={(e) => setDeptViewPermissions(e.detail.selectedOptions.map(option => option.value!))}
                  options={AVAILABLE_DEPARTMENTS}
                  placeholder="Select departments for view access"
                  disabled={isLoading}
                />
              </FormField>

              <FormField 
                label="Edit Access"
                description="Users in these departments can edit the dashboard"
              >
                <Multiselect
                  selectedOptions={deptEditPermissions.map(dept => ({ label: AVAILABLE_DEPARTMENTS.find(d => d.value === dept)?.label || dept, value: dept }))}
                  onChange={(e) => setDeptEditPermissions(e.detail.selectedOptions.map(option => option.value!))}
                  options={AVAILABLE_DEPARTMENTS}
                  placeholder="Select departments for edit access"
                  disabled={isLoading}
                />
              </FormField>
            </ColumnLayout>
          </SpaceBetween>
        </ExpandableSection>

        {/* Permission Summary */}
        <ExpandableSection headerText="Current Access Summary">
          <SpaceBetween size="s">
            {publicAccess && (
              <Box variant="p" color="text-status-success">
                🌐 <strong>Public:</strong> All users can view this dashboard
              </Box>
            )}
            
            {roleViewPermissions.length > 0 && (
              <Box variant="p">
                👥 <strong>Role View Access:</strong> {roleViewPermissions.map(role => 
                  AVAILABLE_ROLES.find(r => r.value === role)?.label || role
                ).join(', ')}
              </Box>
            )}
            
            {roleEditPermissions.length > 0 && (
              <Box variant="p">
                ✏️ <strong>Role Edit Access:</strong> {roleEditPermissions.map(role => 
                  AVAILABLE_ROLES.find(r => r.value === role)?.label || role
                ).join(', ')}
              </Box>
            )}
            
            {deptViewPermissions.length > 0 && (
              <Box variant="p">
                🏢 <strong>Department View Access:</strong> {deptViewPermissions.map(dept => 
                  AVAILABLE_DEPARTMENTS.find(d => d.value === dept)?.label || dept
                ).join(', ')}
              </Box>
            )}
            
            {deptEditPermissions.length > 0 && (
              <Box variant="p">
                🏢 <strong>Department Edit Access:</strong> {deptEditPermissions.map(dept => 
                  AVAILABLE_DEPARTMENTS.find(d => d.value === dept)?.label || dept
                ).join(', ')}
              </Box>
            )}

            <Box variant="p" color="text-body-secondary">
              💡 <strong>Note:</strong> Dashboard owners, admins, and managers always have full access regardless of these settings.
            </Box>
          </SpaceBetween>
        </ExpandableSection>
      </SpaceBetween>
    </Container>
  )
}